import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allComics } from '@/mocks/comics';

const BASE_URL = 'https://hanman.com';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const comic = allComics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const statusColor = comic.status === '完结'
    ? 'bg-secondary-500 text-background-50'
    : 'bg-primary-500 text-background-50';

  const ratingStars = Array.from({ length: 5 }, (_, i) => (
    <i
      key={i}
      className={`text-sm ${i < Math.floor(comic.rating) ? 'ri-star-fill text-accent-500' : i < comic.rating ? 'ri-star-half-line text-accent-500' : 'ri-star-line text-foreground-300'}`}
    ></i>
  ));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: comic.title,
    url: `${BASE_URL}/article/${comic.id}`,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    datePublished: comic.chapters[0]?.date,
    dateModified: comic.updateDate,
    genre: comic.tags,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 5,
      ratingCount: Math.floor(comic.views / 100),
    },
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/ViewAction',
      userInteractionCount: comic.views,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: BASE_URL },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: `${BASE_URL}/category/recommend` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <SEO
        title={`${comic.title}_${comic.author}_韩漫在线观看-韩漫网`}
        description={`${comic.title}，作者${comic.author}，${comic.description.slice(0, 120)}。韩漫网免费韩漫在线观看，漫画网址推荐。`}
        keywords={`${comic.title},${comic.author},${comic.tags.join(',')},韩国漫画,韩漫在线观看,韩漫网`}
        canonicalUrl={`${BASE_URL}/article/${comic.id}`}
        ogType="article"
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '分类浏览', href: '/category/recommend' },
            { label: comic.title },
          ]}
        />

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-full md:w-64 flex-shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                  title={`${comic.title} - 好看的韩国漫画`}
                />
              </div>
            </div>

            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  {comic.title}
                </h1>
                <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${statusColor}`}>
                  {comic.status}
                </span>
              </div>

              <p className="text-sm text-foreground-600 mb-3">
                作者：<strong>{comic.author}</strong>
              </p>

              <div className="flex items-center gap-2 mb-4">
                {ratingStars}
                <span className="text-lg font-bold text-foreground-900 ml-1">{comic.rating}</span>
                <span className="text-xs text-foreground-500 ml-2">
                  <i className="ri-eye-line mr-1"></i>
                  {comic.views >= 10000
                    ? `${(comic.views / 10000).toFixed(1)}万`
                    : comic.views}{' '}
                  次阅读
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {comic.description}
              </p>

              <Link
                to={`/article/${comic.id}`}
                onClick={(e) => e.preventDefault()}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-all duration-300 text-sm whitespace-nowrap cursor-pointer self-start"
              >
                <i className="ri-play-circle-line text-lg"></i>
                开始阅读
              </Link>
            </div>
          </div>

          <section className="mt-12">
            <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">
                共 {comic.chapters.length} 话
              </span>
            </h2>
            <div className="bg-background-100 rounded-xl p-4 md:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.number}
                    className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs font-bold text-foreground-400 bg-background-100 px-2 py-0.5 rounded whitespace-nowrap">
                        第{chapter.number}话
                      </span>
                      <span className="text-sm text-foreground-800 truncate group-hover:text-primary-600 transition-colors duration-200">
                        {chapter.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.date}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {allComics
                .filter((c) => c.id !== comic.id && c.tags.some((tag) => comic.tags.includes(tag)))
                .slice(0, 4)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={related.title}
                      />
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-500 transition-colors duration-200">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-0.5">{related.author}</p>
                  </Link>
                ))}
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}